import { queryOptions } from '@tanstack/react-query'
import { api, listMemories, readMemory } from './apiClient'
import {
  fetchKnowledgeBaseGraph,
  fetchPublishedKnowledgeBases,
  type KnowledgeGraphRef,
} from './knowledgeBaseApi'
import {
  toChatDetail,
  toChatSummary,
  toDashboardDetail,
  toDashboardSummary,
  userText,
} from './adapters'
import type {
  DashboardRead,
  MemoryDetail,
  MemoryList,
  PaginatedArchivedChats,
  PaginatedArchivedProjects,
  PaginatedChats,
  PaginatedDashboards,
  PaginatedMessages,
  ChatSummary,
  PaginatedProjects,
  PaginatedSearchChats,
  PredefinedQuestionRead,
  ProjectFiles,
  ProjectRead,
  SharedChats,
  UsageGranularity,
  UsageSummary,
} from '@/types/api'
import type { DashboardVisuals } from '@/types/dashboard'

/** Shared TanStack Query definitions (plan §8) — non-streaming reads against
 * the /genai/api/v1 backend, adapted to the UI view models. Lists fetch one
 * max-size page (backend cap 100); keyset pagination is a follow-up. */

export interface ModelOption {
  /** Wire value — sent as ConverseInput.model, matched server-side. */
  name: string
  /** Display form of `name` ("sonnet-4.6" → "Sonnet 4.6"). */
  label: string
  desc: string
  /** false → the server rejects ConverseInput.think for this model. */
  thinking: boolean
  default: boolean
}

/** "sonnet-4.6" → "Sonnet 4.6". Display only — never sent to the server. */
export function modelLabel(name: string): string {
  return name
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => (/^[a-z]/.test(w) ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export const DEFAULT_PREDEFINED_QUESTIONS_LIMIT = 4
export const DEFAULT_SEED_NUMBER = Math.floor(Math.random() * 10) + 1

export const DEFAULT_PREDEFINED_QUESTIONS: PredefinedQuestionRead[] = [
  {
    template_id: 'q-dwell-time',
    display_text: 'What is average dwell time per stop, by site?',
    sub_text: 'Across route stops and distribution centers',
    category: 'Route & Distribution',
    icon: 'route',
    sort_order: 10,
  },
  {
    template_id: '6dd4e5c8-cfed-5404-a079-f310a8012249',
    display_text: 'Which pallets have not been scanned recently?',
    sub_text: 'RFID assets with no read in the recent window',
    category: 'Asset & Pallet Management',
    icon: 'box',
    sort_order: 20,
  },
  {
    template_id: 'e89d57f5-84f9-5d34-8e78-8369f3295f95',
    display_text: 'Which lifts are overdue for maintenance?',
    sub_text: 'Against the hour-based schedule and its warning threshold',
    category: 'Fleet Maintenance',
    icon: 'wrench',
    sort_order: 30,
  },
  {
    template_id: '718f682a-a1ba-5fb3-81d2-a3fa94169578',
    display_text: 'Which routes missed the most stops?',
    sub_text: 'Planned stops with no detected arrival',
    category: 'Route & Distribution',
    icon: 'route',
    sort_order: 40,
  },
]

export const predefinedQuestionsQueryOptions = (
  limit: number = DEFAULT_PREDEFINED_QUESTIONS_LIMIT,
  offset: number = 0,
  seed: number = DEFAULT_SEED_NUMBER,
) =>
  queryOptions({
    queryKey: ['predefined-questions', limit, offset],
    staleTime: 5 * 60_000,
    placeholderData: (prev) =>
      prev ?? DEFAULT_PREDEFINED_QUESTIONS.slice(offset, offset + limit),
    queryFn: async () => {
      const res = await api.get<
        PredefinedQuestionRead[] | { questions?: PredefinedQuestionRead[] }
      >(
        `/predefined_questions?limit=${encodeURIComponent(String(limit))}&offset=${encodeURIComponent(String(offset))}&seed=${encodeURIComponent(String(seed))}`,
      )
      if (Array.isArray(res)) return res
      if (res && Array.isArray(res.questions)) return res.questions
      return []
    },
  })

/** GET /models — the org's visible models. Cached for the session; the picker
 * falls back to its static rows while this is loading or if it fails. */

export const modelsQueryOptions = queryOptions({
  queryKey: ['models'],
  staleTime: Infinity,
  queryFn: async () => {
    const models = await api.get<
      {
        name: string
        description?: string
        thinking?: boolean
        default?: boolean
      }[]
    >('/models')
    return models.map((m): ModelOption => ({
      name: m.name,
      label: modelLabel(m.name),
      desc: m.description ?? '',
      thinking: Boolean(m.thinking),
      default: Boolean(m.default),
    }))
  },
})

export const chatsQueryOptions = queryOptions({
  queryKey: ['chats'],
  queryFn: async () => {
    const page = await api.get<PaginatedChats>('/chat/active?limit=100')
    return page.chats.map(toChatSummary)
  },
})

/** GET /chat/active?starred=true — the Chats page "Starred" filter, using
 * the same starred param list_active_chats already accepts. */
export const starredChatsQueryOptions = queryOptions({
  queryKey: ['chats', 'starred'],
  queryFn: async () => {
    const page = await api.get<PaginatedChats>(
      '/chat/active?starred=true&limit=100',
    )
    return page.chats.map(toChatSummary)
  },
})

/** GET /chat/search?query=... — full-text search over chat titles and
 * message content (⌘K palette, query.length >= 3). Matched messages are
 * joined into a preview snippet per chat since the search endpoint doesn't
 * return one directly (unlike /chat/active's ChatSummary.preview). */
export const chatSearchQueryOptions = (query: string) =>
  queryOptions({
    queryKey: ['chats', 'search', query],
    queryFn: async () => {
      const page = await api.get<PaginatedSearchChats>(
        `/chat/search?query=${encodeURIComponent(query)}&limit=20`,
      )
      const snippetsByChat = new Map<string, string[]>()
      for (const m of page.messages) {
        const text = userText(m)
        if (!text) continue
        const list = snippetsByChat.get(m.chat_id) ?? []
        if (list.length < 2) list.push(text.slice(0, 200))
        snippetsByChat.set(m.chat_id, list)
      }
      // Annotated: without it the inferred element type makes `preview`
      // required-but-undefined, which no longer unifies with ChatSummary where
      // it is optional (the palette holds both this and /chat/active results).
      return page.chats.map((c): ChatSummary => ({
        id: c.chat_id,
        title: c.title,
        starred: false,
        updated_at: c.archived_at ?? c.created_at,
        preview: snippetsByChat.get(c.chat_id)?.join(' … '),
      }))
    },
  })

export interface ArchivedChatSummary {
  id: string
  title: string
  archived_at: string
}

export const archivedChatsQueryOptions = queryOptions({
  queryKey: ['chats', 'archived'],
  queryFn: async () => {
    const page = await api.get<PaginatedArchivedChats>(
      '/chat/archive?limit=100',
    )
    return page.chats.map((c): ArchivedChatSummary => ({
      id: c.chat_id,
      title: c.title,
      archived_at: c.archived_at,
    }))
  },
})

export const sharedChatQueryOptions = (token: string) =>
  queryOptions({
    queryKey: ['shared', token],
    queryFn: async () => {
      const page = await api.get<PaginatedMessages>(`/chat/shared/${token}`)
      return toChatDetail(page.chat_id ?? token, page)
    },
  })

export const sharedLinksQueryOptions = queryOptions({
  queryKey: ['chats', 'shared'],
  queryFn: async () => {
    const page = await api.get<SharedChats>('/chat/shared')
    return page.links
  },
})

export const dashboardsQueryOptions = queryOptions({
  queryKey: ['dashboards'],
  queryFn: async () => {
    const page = await api.get<PaginatedDashboards>(
      '/dashboard/active?limit=100',
    )
    return page.dashboards.map(toDashboardSummary)
  },
})

export const publishedKnowledgeBasesQueryOptions = queryOptions({
  queryKey: ['knowledge-bases', 'published'],
  queryFn: () => fetchPublishedKnowledgeBases({ limit: 20 }),
  staleTime: 60_000,
})

/** GET /knowledge_bases/{id}/graph — published model, or draft when the base has no release. */
export function knowledgeBaseGraphQueryOptions(
  kbId: string,
  ref: KnowledgeGraphRef = 'published',
) {
  return queryOptions({
    queryKey: ['knowledge-graph', 'kb', kbId, ref],
    queryFn: () => fetchKnowledgeBaseGraph(kbId, ref),
    staleTime: 5 * 60_000,
  })
}

export const chatDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['chats', id],
    queryFn: async () => {
      const page = await api.get<PaginatedMessages>(`/chat/${id}?limit=2000`)
      return toChatDetail(id, page)
    },
  })

export type ProjectScope = 'mine' | 'org' | 'shared'

export const projectsQueryOptions = (scope: ProjectScope) =>
  queryOptions({
    queryKey: ['projects', scope],
    queryFn: async () => {
      const page = await api.get<PaginatedProjects>(
        `/projects?scope=${scope}&limit=100`,
      )
      return page.projects
    },
  })

export const projectDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['projects', 'detail', id],
    queryFn: () => api.get<ProjectRead>(`/projects/${id}`),
  })

export const projectFilesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['projects', 'files', id],
    queryFn: async () => {
      const page = await api.get<ProjectFiles>(`/projects/${id}/files`)
      return page.files
    },
  })

/** GET /projects/{id}/chats — the chats filed under this project. */
export const projectChatsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['projects', 'chats', id],
    queryFn: async () => {
      const page = await api.get<PaginatedChats>(
        `/projects/${id}/chats?limit=100`,
      )
      return page.chats.map(toChatSummary)
    },
  })

export interface ArchivedProjectSummary {
  id: string
  name: string
  archived_at: string
}

export const archivedProjectsQueryOptions = queryOptions({
  queryKey: ['projects', 'archived'],
  queryFn: async () => {
    const page = await api.get<PaginatedArchivedProjects>(
      '/projects/archive?limit=100',
    )
    return page.projects.map((p): ArchivedProjectSummary => ({
      id: p.project_id,
      name: p.name,
      archived_at: p.archived_at ?? '',
    }))
  },
})

export const memoriesQueryOptions = queryOptions({
  queryKey: ['memories'],
  queryFn: (): Promise<MemoryList> => listMemories(),
})

export const memoryDetailQueryOptions = (name: string) =>
  queryOptions({
    queryKey: ['memories', name],
    queryFn: (): Promise<MemoryDetail> => readMemory(name),
  })

export const usageQueryOptions = (
  granularity: UsageGranularity,
  periods: number,
) =>
  queryOptions({
    queryKey: ['usage', granularity, periods],
    queryFn: () =>
      api.get<UsageSummary>(
        `/usage?granularity=${granularity}&periods=${periods}`,
      ),
  })

export const dashboardDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['dashboards', id],
    queryFn: async () => {
      const dash = await api.get<DashboardRead & DashboardVisuals>(
        `/dashboard/${id}`,
      )
      return toDashboardDetail(dash)
    },
  })
